/**
 * Converts Arabic numerals to English numerals in a string.
 *
 * @param {any} input - The input string containing Arabic numerals
 * @returns {string} The string with Arabic numerals converted to English numerals
 */
export const convertArabicToEnglishNumbers = (input: any): string => {
  if (!input) {
    return ''; // Return an empty string if input is undefined or null
  }

  const arabicToEnglishMap: { [key: string]: string } = {
    '۰': '0',
    '۱': '1',
    '۲': '2',
    '۳': '3',
    '۴': '4',
    '۵': '5',
    '۶': '6',
    '۷': '7',
    '۸': '8',
    '۹': '9',
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9',
  };

  return input
    .toString() // Convert input to string to handle numbers
    .split('')
    .map((char: string) => arabicToEnglishMap[char] || char)
    .join('');
};

export const convertToArabicNumbers = (input: string): string => {
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

  return input.replace(/\d/g, (digit) => arabicNumbers[parseInt(digit)]);
};

/**
 * Formats a number with thousand separators using the en-US locale.
 * Handles various input types and invalid cases gracefully.
 *
 * @param {number | string | null | undefined} num - The number to format
 * @returns {string} Formatted number with thousand separators, or ' - ' for invalid inputs
 */
export const formatWithThousandSeparator = (num: number | string | null | undefined): string => {
  const number = Number(num);
  if (num === '' || num === null || num === undefined || Number.isNaN(number)) return ' - ';
  if (num === 0) return '0';
  return Intl.NumberFormat('en-US').format(number);
};

/**
 * Cleans a number string by converting Arabic numerals to English and preserving the minus sign.
 * Removes all other non-digit characters.
 *
 * @param {string | number} number - The number to clean
 * @returns {string} The cleaned number string
 */
export const cleanNumber = (number?: string | number, forcePositive?: boolean) => {
  if (!number) return '';
  const str = convertArabicToEnglishNumbers(number);
  const isNegative = str.startsWith('-');
  const cleaned = str.replace(/[^\d]/g, '');
  return isNegative && !forcePositive ? `-${cleaned}` : cleaned;
};

export const shortenNumber = (num: number): string => {
  if (num <= 999) {
    return num.toString();
  } else if (num < 1_000_000) {
    const formatted = Math.floor((num / 1000) * 10) / 10;
    return `${formatted % 1 === 0 ? formatted.toFixed(0) : formatted}K`;
  } else {
    const formatted = Math.floor((num / 1_000_000) * 10) / 10;
    return `${formatted % 1 === 0 ? formatted.toFixed(0) : formatted}M`;
  }
};
